import React, { createContext, useContext, useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import API_BASE_URL from '../config/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false); // Default to light mode for unauthenticated users
  const [isLoading, setIsLoading] = useState(true);

  // Load theme preference from database on mount and when auth token changes
  useEffect(() => {
    const loadThemeFromDatabase = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.log('🎨 No auth token found, using default light theme');
          setIsDarkMode(false); // Default to light mode for unauthenticated users
          setIsLoading(false);
          return;
        }
        
        console.log('🎨 Loading theme from database...');

        const response = await fetch(`${API_BASE_URL}/api/get-theme`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const userTheme = data.theme;
          console.log('🎨 Theme loaded from database:', userTheme);
          const newIsDarkMode = userTheme === 'dark';
          console.log('🎨 Setting isDarkMode to:', newIsDarkMode);
          setIsDarkMode(newIsDarkMode);
        } else {
          console.log('❌ Failed to load theme from database, using default light theme');
          setIsDarkMode(false);
        }
      } catch (error) {
        console.error('Error loading theme from database:', error);
        setIsDarkMode(false); // Default to light mode on error
      } finally {
        setIsLoading(false);
      }
    };

    loadThemeFromDatabase();
  }, []);

  // Function to reload theme from database (can be called externally)
  const reloadTheme = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('🎨 No auth token found for theme reload');
        return;
      }
      
      console.log('🎨 Manually reloading theme from database...');
      const response = await fetch(`${API_BASE_URL}/api/get-theme`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const userTheme = data.theme;
        console.log('🎨 Theme reloaded from database:', userTheme);
        const newIsDarkMode = userTheme === 'dark';
        console.log('🎨 Setting isDarkMode to:', newIsDarkMode);
        setIsDarkMode(newIsDarkMode);
      } else {
        console.log('❌ Failed to reload theme from database');
      }
    } catch (error) {
      console.error('Error reloading theme from database:', error);
    }
  };

  const toggleTheme = async () => {
    const newTheme = !isDarkMode;
    console.log('🎨 Toggling theme from', isDarkMode, 'to', newTheme);
    setIsDarkMode(newTheme);
    
    // Save to database
    await saveThemeToDatabase(newTheme ? 'dark' : 'light');
  };

  const setTheme = async (theme) => {
    const newIsDarkMode = theme === 'dark';
    setIsDarkMode(newIsDarkMode);
    
    // Save to database
    await saveThemeToDatabase(theme);
  };

  const saveThemeToDatabase = async (theme) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('No auth token found, theme preference not saved to database');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/update-theme`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ theme })
      });

      if (response.ok) {
        console.log('🎨 Theme preference saved to database:', theme);
      } else {
        console.error('❌ Failed to save theme preference to database');
        const errorData = await response.json();
        console.error('❌ Error details:', errorData);
      }
    } catch (error) {
      console.error('Error saving theme preference to database:', error);
    }
  };

  useEffect(() => {
    // Apply theme to body element
    console.log('🎨 Applying theme to body, isDarkMode:', isDarkMode);
    if (isDarkMode) {
      document.body.classList.add('dark-theme');
      console.log('🎨 Added dark-theme class to body');
    } else {
      document.body.classList.remove('dark-theme');
      console.log('🎨 Removed dark-theme class from body');
    }
  }, [isDarkMode]);

  const value = {
    isDarkMode,
    toggleTheme,
    setTheme,
    reloadTheme,
    theme: isDarkMode ? 'dark' : 'light',
    isLoading
  };

  // Always provide context, even during loading
  return (
    <ThemeContext.Provider value={value}>
      {isLoading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: 'var(--surface-1)'
        }}>
          <CircularProgress 
            size={60}
            thickness={4}
            sx={{ 
              color: '#6c3082' // Primary purple color
            }}
          />
        </div>
      ) : (
        children
      )}
    </ThemeContext.Provider>
  );
}; 